/**
 * The client switcher (design 10.2): a combobox in the header that lists only the person's memberships, with
 * their roles and a dot when something is overdue, and navigates by URL. "All clients" goes to the portfolio.
 * Keyboard: the arrow keys move through the list, Enter chooses (in the search box, the first client that matches),
 * Escape closes and returns to the button. The button's accessible name carries the client it shows.
 * "Recent" is a convenience kept in localStorage and only ever shows clients the server listed just now.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, Search } from 'lucide-react'

import { useT } from '../i18n/LanguageProvider'
import type { MessageKey } from '../i18n/en'
import { api } from '../lib/api'
import { clientOf, readRecent, rememberRecent, sameViewIn } from '../lib/switcher'

export interface ClientRow {
  code: string
  name: string
  status: string
  is_fictional: boolean
  roles: string[]
  overdue?: number
}

export function ClientSwitcher() {
  const t = useT()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const current = clientOf(pathname)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [clients, setClients] = useState<ClientRow[] | null>(null)
  const [failed, setFailed] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLUListElement>(null)

  useEffect(() => {
    api.get<{ clients: ClientRow[] }>('portfolio/clients').then(
      (r) => {
        setClients(r.clients)
        setFailed(false)
      },
      () => setFailed(true),
    )
  }, [open])

  useEffect(() => {
    if (current) rememberRecent(current)
  }, [current])

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (clients ?? []).filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.includes(q))
  }, [clients, query])
  const recent = readRecent()
    .map((code) => (clients ?? []).find((c) => c.code === code))
    .filter((c): c is ClientRow => Boolean(c) && !query)
  const currentName = (clients ?? []).find((c) => c.code === current)?.name

  const go = (code: string | null) => {
    setOpen(false)
    setQuery('')
    navigate(code ? sameViewIn(pathname, code) : '/portfolio')
  }

  // Enter in the search box chooses the first client that matches what was typed.
  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !query.trim()) return
    const first = filtered[0]
    if (!first) return
    e.preventDefault()
    go(first.code)
  }
  const shown = currentName ?? (current ? current : t('switcher.all'))

  const options = () => Array.from(list.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [])
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      button.current?.focus()
      return
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const all = options()
    if (all.length === 0) return
    const at = all.indexOf(document.activeElement as HTMLButtonElement)
    const next = e.key === 'ArrowDown' ? (at + 1) % all.length : at <= 0 ? all.length - 1 : at - 1
    all[next]?.focus()
  }

  const roleList = (c: ClientRow) => c.roles.map((r) => t(`role.${r}` as MessageKey)).join(' · ')
  const row = (c: ClientRow, prefix: string) => (
    <li key={`${prefix}-${c.code}`} role="presentation">
      <button type="button" role="option" aria-selected={c.code === current} onClick={() => go(c.code)} className="flex w-full items-start justify-between gap-2 rounded px-2 py-1.5 text-left hover:bg-ol-page focus:bg-ol-page">
        <span className="min-w-0">
          <span className="block truncate">
            {c.name}
            {c.is_fictional && <span className="ml-2 text-sm text-ol-text-3">{t('app.fictional')}</span>}
          </span>
          <span className="block text-sm text-ol-text-3">{roleList(c)}</span>
        </span>
        {(c.overdue ?? 0) > 0 && (
          <span className="flex shrink-0 items-center gap-1 text-sm text-ol-red">
            <span aria-hidden="true">{'●'}</span>
            {t('switcher.overdue', { n: c.overdue ?? 0 })}
          </span>
        )}
      </button>
    </li>
  )

  return (
    <div className="relative" ref={box} onKeyDown={onKey}>
      <button
        ref={button}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('switcher.label')}: ${shown}`}
        onClick={() => setOpen((o) => !o)}
        className="flex max-w-[16rem] items-center gap-2 rounded-md border border-ol-border bg-white px-3 py-1.5 text-[15px] text-ol-text hover:bg-ol-page"
      >
        <span className="truncate">{shown}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-[min(22rem,90vw)] rounded-md border border-ol-border bg-white p-2 shadow-lg">
          <label className="flex items-center gap-2 rounded-md border border-ol-border px-2">
            <Search size={16} aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder={t('switcher.search')}
              aria-label={t('switcher.search')}
              className="w-full py-1.5 text-[15px] outline-none"
            />
          </label>
          <ul ref={list} role="listbox" aria-label={t('switcher.choose')} className="mt-2 max-h-80 overflow-y-auto">
            <li role="presentation">
              <button type="button" role="option" aria-selected={!current} onClick={() => go(null)} className="w-full rounded px-2 py-1.5 text-left hover:bg-ol-page focus:bg-ol-page">
                {t('switcher.all')}
              </button>
            </li>
            {recent.length > 0 && (
              <>
                <li className="px-2 pt-2 text-sm font-medium text-ol-text-3" role="presentation">
                  {t('switcher.recent')}
                </li>
                {recent.map((c) => row(c, 'r'))}
              </>
            )}
            <li className="px-2 pt-2 text-sm font-medium text-ol-text-3" role="presentation">
              {t('switcher.allOfYours', { n: clients?.length ?? 0 })}
            </li>
            {failed && <li role="presentation" className="px-2 py-1.5 text-ol-red">{t('switcher.failed')}</li>}
            {!clients && !failed && <li role="presentation" className="px-2 py-1.5 text-ol-text-2">{t('common.loading')}</li>}
            {clients && clients.length === 0 && <li role="presentation" className="px-2 py-1.5 text-ol-text-2">{t('switcher.none')}</li>}
            {clients && clients.length > 0 && filtered.length === 0 && <li role="presentation" className="px-2 py-1.5 text-ol-text-2">{t('switcher.noMatch')}</li>}
            {filtered.map((c) => row(c, 'a'))}
          </ul>
        </div>
      )}
    </div>
  )
}
